import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /** Disables the add to cart button. */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /** Enables the add to cart button. */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();
    // if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  /** Animates the fly to cart animation. */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  /** Animates the add to cart button. */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** FAST VARIANT INDEX (added) **/
  _variantIndex = null;  // Map key: "opt1|opt2|opt3" -> { id, available, media }
  _product = null;
  _lastResolvedVariantId = '';

  connectedCallback() {
    super.connectedCallback();

    // --- Build a local variant lookup so we can update variantId synchronously ---
    const jsonEl =
      this.querySelector('[data-product-json]') ||
      this.closest('.shopify-section, dialog')?.querySelector('[data-product-json]') ||
      document.querySelector('[data-product-json]');

    if (jsonEl && !this._variantIndex) {
      try {
        const product = JSON.parse(jsonEl.textContent);
        this._product = product;
        const idx = new Map();
        product.variants?.forEach(v => {
          const key = [v.option1, v.option2, v.option3].filter(Boolean).join('|');
          idx.set(key, {
            id: String(v.id),
            available: v.available !== false,
            media: v.featured_media?.preview_image?.src || v.featured_image?.src || null,
          });
        });
        this._variantIndex = idx;
      } catch (e) {
        // ignore JSON errors
      }
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** Helpers (added) **/
  getSelectedOptions = () => {
    // Adjust selectors to your picker markup: elements with [data-option-index]
    const picks = [];
    const optNodes = this.querySelectorAll('[data-option-index]');
    optNodes.forEach(node => {
      if (node.tagName === 'SELECT') {
        picks.push(node.value);
      } else {
        const checked = node.querySelector(':checked');
        if (checked) picks.push(checked.value);
      }
    });
    return picks.join('|');
  };

  /**
   * Handles the submit event for the product form.
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const addToCartBtn = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (addToCartBtn?.disabled) return; // correct disabled guard

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    // Guard against stale/empty variant id (kept)
    const variantIdInput = this.refs.variantId;
    if (!variantIdInput?.value) {
      this.#setLiveRegionText(Theme.translations.select_variant || 'Please select a variant');
      return;
    }
    if (this._lastResolvedVariantId && variantIdInput.value !== this._lastResolvedVariantId) {
      // UI hasn’t finished updating — prevent wrong variant add
      return;
    }

    const formData = new FormData(form);

    // Build sections once
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(response.message));
          }

          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            addToCartTextError?.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );
          return;
        } else {
          const id = formData.get('id');
          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }
          if (!id) throw new Error('Form ID is required');

          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
            this.#setLiveRegionText(addedText);
            setTimeout(() => this.#clearLiveRegionText(), 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /** @param {*} text */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Handles variant updates coming from the theme (network + HTML morph path).
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    const available = !!(event.detail.resource && event.detail.resource.available !== false);

    if (available) {
      addToCartButtonContainer?.enable?.();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute?.('hidden');
    } else {
      addToCartButtonContainer?.disable?.();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute?.('hidden', 'true');
    }

    // Keep variantId in sync (but it's already set synchronously in #onVariantSelected)
    variantId.value = event.detail.resource?.id ?? '';
    this._lastResolvedVariantId = variantId.value;

    // OPTIONAL: avoid morphing the entire add-to-cart button (can be costly)
    if (currentAddToCartButton && newAddToCartButton) {
      // morph(currentAddToCartButton, newAddToCartButton); // consider leaving this disabled
    }

    const productVariantMedia = event.detail.resource?.featured_media?.preview_image?.src;
    if (productVariantMedia) {
      addToCartButtonContainer?.setAttribute(
        'data-product-variant-media',
        productVariantMedia + '&width=100'
      );
    }
  };

  /**
   * FAST PATH: immediately set variantId from local index when user selects options.
   * Disables waiting for network/morph so the hidden input updates in ~0–1 frames.
   */
  #onVariantSelected = () => {
    // Skip gift cards / donations (data-is-gift-card="true")
    if (this.dataset.isGiftCard === 'true') return;

    // Compute key and find the variant synchronously
    const key = this.getSelectedOptions ? this.getSelectedOptions() : '';
    const hit = key && this._variantIndex ? this._variantIndex.get(key) : null;

    if (!hit?.id) {
      // Incomplete selection; keep disabled and clear id
      this.refs.addToCartButtonContainer?.disable?.();
      this.refs.variantId.value = '';
      this._lastResolvedVariantId = '';
      this.#setLiveRegionText(Theme.translations.select_variant || 'Please select a variant');
      return;
    }

    // Set the hidden input NOW
    this.refs.variantId.value = hit.id;
    this._lastResolvedVariantId = hit.id;

    // Button + accelerated checkout visibility instantly from local data
    if (hit.available) {
      this.refs.addToCartButtonContainer?.enable?.();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute?.('hidden');
    } else {
      this.refs.addToCartButtonContainer?.disable?.();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute?.('hidden', 'true');
    }

    // Optional: media for fly-to-cart right away
    if (hit.media) {
      this.refs.addToCartButtonContainer?.setAttribute('data-product-variant-media', hit.media + '&width=100');
    }

    // Do NOT submit here; let user click or your existing flow call handleSubmit
    // Your #onVariantUpdate will still run later to refresh other UI bits.
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */ source;
  /** @type {Element} */ destination;

  connectedCallback() { this.#animate(); }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = { x: rect.width / 2, y: rect.height / 2 };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 };
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 };

    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // ms

    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t
 * @param {{x: number, y: number}} p0
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @param {{x: number, y: number}} p3
 * @returns {{x: number, y: number}}
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
